import React, { useState } from "react";
import { useFormik } from "formik";
import BeneficiarySearchNSelect from "../../Eligibility/components/requestEligibility/BeneficiarySearchNSelect";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaFilter, FaUndo } from "react-icons/fa";
import CustomFilterInput from "../../../shared/CustomFilterInput";
import AppButton from "../../../shared/AppButton";

const DEFAULT_LIMIT = 15;

const cleanFilterInput = (input) =>
  Object.keys(input).reduce((acc, key) => {
    const value = input[key];
    if (
      value !== "" &&
      value !== null &&
      value !== undefined &&
      !(key === "is_followup_req" && value === false)
    ) {
      acc[key] = value;
    }
    return acc;
  }, {});

const toFormValues = (filters = {}) => ({
  fromDate: filters?.from_date || "",
  toDate: filters?.to_date || "",
  fromDateOrdered: filters?.from_date_ordered || "",
  toDateOrdered: filters?.to_date_ordered || "",
  tpa: filters?.tpa_id || "",
  payer: filters?.payer_id || "",
  type: filters?.type || "",
  beneficiary: filters?.patient_id || "",
  status: filters?.status || "",
  bundleID: filters?.request_bundle_id || "",
  batchID: filters?.batch_id || "",
  tpRefNo: filters?.tp_ref_no || "",
  isFollowUp: Boolean(filters?.is_followup_req),
  outcome: filters?.outcome || "",
  referralType: filters?.referral_type || "",
  isValid: true,
});

const toFilterInput = (values, activeTab, limit = DEFAULT_LIMIT, page = 1) => {
  const input = {
    from_date: values.fromDate,
    to_date: values.toDate,
    tpa_id: values.tpa,
    payer_id: values.payer,
    type: values.type,
    patient_id: values.beneficiary,
    batch_id: values.batchID,
    tp_ref_no: values.tpRefNo,
    ...(activeTab && { outcome: activeTab }),
    from_date_ordered: values?.fromDateOrdered,
    to_date_ordered: values?.toDateOrdered,
    status: values.status,
    request_bundle_id: values.bundleID,
    referral_type: values.referralType,
    is_followup_req: values?.isFollowUp,
    limit,
    page,
  };

  return cleanFilterInput(input);
};

function ClaimsFilter({
  setClaimsFilters,
  claimsFilters,
  refetch,
  setLoading,
  setPageNo,
  activeTab,
  fromDate,
  toDate,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [payers, setPayers] = useState([]);
  const [patientSearchResetKey, setPatientSearchResetKey] = useState(0);

  const addData = async (values) => {
    const limit = claimsFilters?.limit || DEFAULT_LIMIT;
    const obj = toFilterInput(values, activeTab, limit, 1);

    localStorage.setItem("claimsListFilters", JSON.stringify(values));
    localStorage.setItem(
      "claimsListPagination",
      JSON.stringify({
        pageNo: 1,
        limit,
      })
    );
    localStorage.setItem("claimsFiltersSaved", JSON.stringify(obj));
    setClaimsFilters(obj);
  };

  React.useEffect(() => {
    const payersData = JSON.parse(localStorage.getItem("payersData"));
    if (payersData && payersData.length > 0) {
      setPayers(payersData);
    } else {
      navigate("/dashboard");
    }
  }, []);

  const types = [
    { id: "institutional", name: t("Institutional") },
    { id: "oral", name: t("Dental") },
    { id: "pharmacy", name: t("Pharmacy") },
    { id: "professional", name: t("Professional") },
    { id: "vision", name: t("Optical") },
  ];

  const statuses = [
    { id: "approved", name: t("Approved") },
    { id: "partial Approved", name: t("Partial Approved") },
    { id: "rejected", name: t("Rejected") },
    { id: "pending", name: t("Pending") },
    { id: "pended", name: t("Pended") },
    { id: "cancelled", name: t("Cancelled") },
  ];
  const referralTypes = [
    { id: "referred_in", name: t("Referral In") },
    { id: "referred_out", name: t("Referral Out") },
    { id: "in_out", name: t("In/Out") },
  ];

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = useFormik({
    enableReinitialize: true,
    initialValues: toFormValues(claimsFilters),
    onSubmit: (values) => {
      addData(values);
    },
  });

  const handleResetForm = async () => {
    const limit = claimsFilters?.limit || DEFAULT_LIMIT;
    const obj = {
      from_date: fromDate,
      to_date: toDate,
      tpa_id: "",
      payer_id: "",
      type: "",
      patient_id: "",
      batch_id: "",
      tp_ref_no: "",
      ...(activeTab && { outcome: activeTab }),
      from_date_ordered: "",
      to_date_ordered: "",
      status: "",
      request_bundle_id: "",
      is_followup_req: "",
      limit,
      page: 1,
    };
    const cleanObj = cleanFilterInput(obj);
    const resetValues = toFormValues(obj);

    localStorage.removeItem("claimsListFilters");
    localStorage.setItem(
      "claimsListPagination",
      JSON.stringify({
        pageNo: 1,
        limit,
      })
    );
    localStorage.setItem("claimsFiltersSaved", JSON.stringify(cleanObj));
    resetForm({ values: resetValues });
    setPatientSearchResetKey((key) => key + 1);
    setPageNo?.(1);
    setClaimsFilters(cleanObj);

    if (refetch) {
      try {
        setLoading?.(true);
        await refetch({ input: cleanObj });
      } finally {
        setLoading?.(false);
      }
    }
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-x-1.5 gap-y-1.5 pb-2">
        <CustomFilterInput
          label={t("Submission Date")}
          name="fromDate"
          value={values.fromDate}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("From Date")}
          type="date"
        />
        <CustomFilterInput
          label={t("To Date")}
          name="toDate"
          value={values.toDate}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("To Date")}
          type="date"
        />
        <CustomFilterInput
          label={t("Ordered Date")}
          name="fromDateOrdered"
          value={values.fromDateOrdered}
          onChange={handleChange}
          placeholder={t("From Order Date")}
          type="text"
          onFocus={(e) => (e.target.type = "date")}
          onBlur={(e) => {
            e.target.type = "text";
            handleBlur(e);
          }}
        />
        <CustomFilterInput
          label={t("To Order Date")}
          name="toDateOrdered"
          value={values.toDateOrdered}
          onChange={handleChange}
          placeholder={t("To Order Date")}
          type="text"
          onFocus={(e) => (e.target.type = "date")}
          onBlur={(e) => {
            e.target.type = "text";
            handleBlur(e);
          }}
        />
        <CustomFilterInput
          label={t("Select TPA")}
          name="tpa"
          value={values.tpa}
          onChange={(event) => {
            setFieldValue("tpa", event.target.value);
            setFieldValue("payer", "");
          }}
          onBlur={handleBlur}
          placeholder={t("All TPAs")}
          isSelect
          renderOptions={() =>
            payers.filter((category) => category?.id).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name_en} ({category.license_id})
              </option>
            ))
          }
        />
        <CustomFilterInput
          label={t("Select Payer")}
          name="payer"
          value={values.payer}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Select Payer")}
          isSelect
          renderOptions={() =>
            payers.filter((category) => !values.tpa || category.id === values.tpa).map((category) => (
              <optgroup
                key={category.id}
                label={
                  category?.name_en
                    ? `${category?.name_en} (${category?.license_id})`
                    : ""
                }
              >
                {category?.payers?.map((insurance, idx) => (
                  <option key={`${insurance.id}-${idx}`} value={insurance.id}>
                    {insurance?.name_en} ({insurance?.license_id})
                  </option>
                ))}
              </optgroup>
            ))
          }
        />
        <CustomFilterInput
          label={t("Select Type")}
          name="type"
          value={values.type}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Select Type")}
          isSelect
          options={types}
        />
        <CustomFilterInput
          label={t("Batch ID")}
          name="batchID"
          value={values.batchID}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Batch ID")}
          type="text"
        />
        <CustomFilterInput
          label={t("Claim Reference")}
          name="tpRefNo"
          value={values.tpRefNo}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Claim Reference")}
          type="text"
        />
        <CustomFilterInput
          label={t("Select Status")}
          name="status"
          value={values.status}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Select Status")}
          isSelect
          options={statuses}
        />
        <CustomFilterInput
          label={t("Request Bundle ID")}
          name="bundleID"
          value={values.bundleID}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("Request Bundle ID")}
          type="text"
        />
        <div className="flex items-center justify-between gap-1">
          <CustomFilterInput
            label={t("Referral Type")}
            name="referralType"
            value={values.referralType}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={t("Referral Type")}
            isSelect
            options={referralTypes}
            parentClassName="w-full"
          />

          {/* Is Follow Up Toggle */}
          <div className="flex flex-col w-full">
          <label className="block font-semibold text-[12px] text-gray-700 mb-0.5 capitalize">
            {t("Follow Up")}
          </label>
          <div className="flex justify-between items-center bg-slate-100/50 border !border-slate-200 rounded-[6px] px-2 !h-[28px] !py-0">
            <div className="text-xs font-medium text-gray-700 capitalize tracking-tight">
              {t("Follow Up")}
            </div>
            <div className="flex items-center">
              <label
                htmlFor="toggle-followup"
                className="relative inline-flex items-center cursor-pointer scale-[0.65] origin-right"
              >
                <input
                  type="checkbox"
                  id="toggle-followup"
                  name="isFollowUp"
                  className="sr-only peer"
                  checked={Boolean(values.isFollowUp)}
                  onChange={handleChange}
                />
                <div
                  className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer
                    peer-checked:!bg-[#0070C2]
                    peer-checked:after:translate-x-[20px]
                    rtl:peer-checked:after:-translate-x-[20px]
                    after:content-[''] after:absolute after:top-[2px] after:start-[2px]
                    after:bg-white after:border-gray-300 after:border after:rounded-full
                    after:h-5 after:w-5 after:transition-all after:duration-300"
                ></div>
              </label>
            </div>
          </div>
          </div>
        </div>
        <div className="col-span-2 flex items-center gap-2">
          <div className="flex-1">
            <label className="block font-semibold text-[12px] text-gray-700 mb-0.5 capitalize truncate">
            {t("Search Patient")}
          </label>
          <BeneficiarySearchNSelect
            key={patientSearchResetKey}
            setFieldValue={(val) => {
              setFieldValue("beneficiary", val?.id);
            }}
            name="eligibility"
          />
          </div>
           <div className="flex items-center gap-2 mt-6">
          <AppButton
            title={t("Apply Filter")}
            onClick={handleSubmit}
            startIcon={<FaFilter className="!text-[12px]" />}
          />
            <AppButton
            title={t("Reset")}
            variant="reset"
            onClick={handleResetForm}
            startIcon={<FaUndo className="!text-[12px]" />}
            className="hover:!bg-slate-100"
          />
        </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(ClaimsFilter);
